import React, { useEffect, useState } from "react";
import { Input, DatePicker, Select, Row, Col } from "antd";
import moment from "moment";
import { CalendarOutlined } from "@ant-design/icons";
import "./Filters.scss";
import actions from "../../../redux/dashboardLogger/actions";
import { useDispatch } from "react-redux";

const { Option } = Select;

const logLevels = ["info", "warn", "error", "debug"];
const methods = ["GET", "POST", "PUT", "DELETE"];

const Filters = ({ tableParams, setTableParams }) => {
  const { getDashboardLogs } = actions;
  const dispatch = useDispatch();
  const [fromDate, setFromDate] = React.useState(null);
  const [toDate, setToDate] = React.useState(null);
  const [queryParams, setQueryParams] = useState({
    level: "",
    method: "",
    search: "",
  });
  const handleFromDateChange = (date) => {
    setFromDate(date);
    if (date > toDate) {
      setToDate(null);
    }
  };

  const handleToDateChange = (date) => {
    if (date?.isAfter(moment(), "day")) {
      setToDate(moment());
    } else if (date?.isSame(moment(), "day")) {
      if (
        date?.isSame(fromDate, "day") &&
        date?.hour() === 0 &&
        date?.minute() === 0 &&
        date?.second() === 0
      ) {
        setToDate(
          date
            .clone()
            .hour(moment().hour())
            .minute(moment().minute())
            .second(moment().second())
        );
      } else if (
        date?.isSame(fromDate, "day") &&
        (date?.hour() > moment().hour() ||
          (date?.hour() === moment().hour() &&
            date?.minute() > moment().minute()) ||
          (date?.hour() === moment().hour() &&
            date?.minute() === moment().minute() &&
            date?.second() > moment().second()))
      ) {
        setToDate(
          date
            .clone()
            .hour(moment().hour())
            .minute(moment().minute())
            .second(moment().second())
        );
      } else if (
        date?.hour() > moment().hour() ||
        (date?.hour() === moment().hour() &&
          date?.minute() > moment().minute()) ||
        (date?.hour() === moment().hour() &&
          date?.minute() === moment().minute() &&
          date?.second() > moment().second())
      ) {
        setToDate(
          date
            .clone()
            .hour(moment().hour())
            .minute(moment().minute())
            .second(moment().second())
        );
      } else {
        setToDate(date);
      }
    } else {
      if (
        date?.isSame(fromDate, "day") &&
        date?.hour() === 0 &&
        date?.minute() === 0 &&
        date?.second() === 0
      ) {
        setToDate(date.clone().hour(23).minute(59).second(59));
      } else {
        setToDate(date);
      }
    }
    if (date && date < fromDate) {
      setFromDate(null);
    }
  };

  const disabledToDate = (current) => {
    return (
      (fromDate && current && current < fromDate?.startOf("day")) ||
      current > moment().endOf("day")
    );
  };

  const disabledTime = (currentDate) => {
    const now = moment();
    if (currentDate.isSame(now, "day")) {
      return {
        disabledHours: () =>
          Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour > now.hour()
          ),
        disabledMinutes: (selectedHour) => {
          if (selectedHour === now.hour()) {
            return Array.from({ length: 60 }, (_, i) => i).filter(
              (minute) => minute > now.minute()
            );
          }
          return [];
        },
        disabledSeconds: () => [],
      };
    }
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
      disabledSeconds: () => [],
    };
  };

  const disabledDate = (current) => {
    return current && current > moment().endOf("day");
  };

  useEffect(() => {
    // When search changes, reset to page 1
    setTableParams((prev) => ({ ...prev, current: 1, pageSize: 10 }));
  }, [queryParams]);

  //   apply filters with useEffect
  useEffect(() => {
    if (
      !fromDate &&
      !toDate &&
      !queryParams.level &&
      !queryParams.method &&
      !queryParams.search &&
      tableParams
    ) {
      dispatch(
        getDashboardLogs(
          tableParams.current,
          tableParams.pageSize,
          fromDate
            ? encodeURIComponent(fromDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"))
            : undefined,
          toDate
            ? encodeURIComponent(toDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"))
            : undefined,
          ...Object.values(queryParams)
        )
      );
    } else if ((fromDate && toDate) || (!fromDate && !toDate)) {
      dispatch(
        getDashboardLogs(
          tableParams.current,
          tableParams.pageSize,
          fromDate
            ? encodeURIComponent(fromDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"))
            : undefined,
          toDate
            ? encodeURIComponent(toDate.format("YYYY-MM-DDTHH:mm:ss.SSSZ"))
            : undefined,
          ...Object.values(queryParams)
        )
      );
    }
  }, [dispatch, fromDate, toDate, queryParams, tableParams]);

  return (
    <div className="logger-filters-card">
      <Row gutter={[16, 16]} align="middle" justify="start">
        <Col xs={24} sm={12} md={6}>
          <Input.Search
            placeholder="Search logs"
            allowClear
            onSearch={(value) =>
              setQueryParams((prev) => ({ ...prev, search: value }))
            }
            className="logger-filter-search"
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div className="logger-filter-date-group">
            <DatePicker
              value={fromDate}
              className="logger-filter-date"
              onChange={handleFromDateChange}
              disabledDate={disabledDate}
              placeholder="From (DD-MM-YYYY HH:mm:ss)"
              showTime
              format="DD-MM-YYYY HH:mm:ss"
              suffixIcon={<CalendarOutlined style={{ color: "#073763" }} />}
            />
            <span className="logger-filter-date-sep">to</span>
            <DatePicker
              value={toDate}
              className="logger-filter-date"
              onChange={handleToDateChange}
              disabledDate={disabledToDate}
              placeholder="To (DD-MM-YYYY HH:mm:ss)"
              showTime={{ format: "HH:mm:ss", disabledTime: disabledTime }}
              format="DD-MM-YYYY HH:mm:ss"
              suffixIcon={<CalendarOutlined style={{ color: "#073763" }} />}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            placeholder="Level"
            onChange={(value) =>
              setQueryParams((prev) => ({ ...prev, level: value }))
            }
            allowClear
            className="logger-filter-select"
          >
            {logLevels.map((level) => (
              <Option key={level} value={level}>
                {level}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Select
            placeholder="Method"
            onChange={(value) =>
              setQueryParams((prev) => ({ ...prev, method: value }))
            }
            allowClear
            className="logger-filter-select"
          >
            {methods.map((method) => (
              <Option key={method} value={method}>
                {method}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
    </div>
  );
};

export default Filters;
